import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { resetPasswordSuccess, setLoading } from '../../features/authSlice';
import CarouselComponent from '../../common/components/CarouselComponent';
import CommonButton from '../../common/components/CommonButton';
import CommonDialog from '../../common/components/CommonDialog';
import ResetPasswordForm from './ResetPasswordForm';
import { resetPassword } from '../../api/authApi';
import LoadingSpinner from '../../common/components/LoadingSpinner';
import { showNotification } from '../../common/components/Notification/ToastifyNotification';

const ResetPassword = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { loading } = useSelector((state) => state.auth);
  const [open, setOpen] = useState(false);
  const searchParams = new URLSearchParams(location.search);
  const details = {
    token: searchParams.get('token'),
    user_type: searchParams.get('type'),
  };

  const handleSubmit = async (values) => {
    try {
      dispatch(setLoading({
        loading: true
      }));
      const response = await resetPassword({
        token: details.token,
        newPassword: values.password,
        confirmPassword: values.confirmpassword
      });
      if (response.success) {
        dispatch(resetPasswordSuccess());
        setOpen(true);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
      dispatch(setLoading({
        loading: false
      }));
      showNotification(errorMessage, 'error');
    }
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
      }}
    >
      {loading && (
        <LoadingSpinner />
      )}
      {open && (
        <CommonDialog
          open={open}
          size="xs"
          content={(
            <center>
              <img
                src='/Images/success.png'
                alt='Check Circle'
                style={{ width: '70px', height: '70px', marginTop: 20 }}
              />
              <Typography sx={{ fontWeight: 600, fontSize: '22px', mt: 0.5 }}>
                Password Updated Successfully
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph mt={0.3}>
                Your password has been changed. You can now use your new password to sign in.
              </Typography>
            </center>
          )}
          actionButtons={(
            <CommonButton
              label="Ok"
              size="large"
              handleClick={() => {
                setOpen(false);
                if (details.user_type === 'Mentor') {
                  navigate('/mentor');
                } else {
                  navigate('/login');
                }
              }}
            />
          )}
        />
      )}
      <Paper
        elevation={isMobile ? 0 : 3}
        sx={{
          width: '100%',
          height: '100%',
          borderRadius: 0,
          overflow: 'hidden',
        }}
      >
        <Grid container sx={{ height: '100%' }}>
          {/* Left Side - Carousel */}
          <Grid
            item
            xs={12}
            md={6.5}
            sx={{
              height: '100%',
              overflow: 'hidden',
              display: { xs: isMobile ? 'none' : 'flex', md: 'flex' },
            }}
          >
            <CarouselComponent />
          </Grid>
          <Grid
            item
            xs={12}
            md={5.5}
            sx={{
              height: '100%',
              overflowY: 'auto',
              display: 'flex',
              backgroundColor: 'white',
            }}
          >
            <ResetPasswordForm
              onSubmit={handleSubmit}
              loading={loading}
              details={details}
            />
          </Grid>
        </Grid>
        {isMobile && (
          <Box sx={{ height: '100%', overflowY: 'auto' }}>
            <ResetPasswordForm
              onSubmit={handleSubmit}
              loading={loading}
              details={details}
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default ResetPassword;